import type { UserLimits } from '../../store/api/usageApi';

/**
 * The limits that apply to the caller, as the server describes them (GET
 * /usage/me → limits): the summary, what counts and what does not, and the
 * usage the enforcer compares. Nothing here is computed or typed on the client.
 *
 * Organization limits (scope 'org', e.g. active training modules) are the
 * workspace's: they show "used / max" when the server sent the workspace's
 * count (the caller may see it), otherwise the limit alone, marked per
 * workspace — the server's explanation then says so (limitCopy.orgLimitScopeNote).
 */
export function PlanLimitsCard({ limits }: { limits: UserLimits | null }) {
  return (
    <div className="card lg:col-span-2" data-testid="plan-limits">
      <div className="card-header flex justify-between items-center">
        <h2 className="font-semibold text-gray-900">Plan Limits</h2>
        {limits && <span className="text-sm text-gray-500">{limits.planName} plan</span>}
      </div>
      <div className="card-body space-y-4">
        {!limits ? (
          <p className="text-sm text-gray-500">Your plan limits could not be loaded right now.</p>
        ) : (
          <>
            {!limits.enforced && (
              <p className="text-sm text-amber-700">Plan limits are switched off in this development environment.</p>
            )}
            <ul className="space-y-4">
              {limits.limits.map((l) => (
                <li key={l.key} data-testid={`plan-limit-${l.key}`}>
                  <div className="flex justify-between items-baseline gap-4">
                    <span className="text-sm font-medium text-gray-900">{l.summary}</span>
                    {l.used === null ? (
                      <span className="text-sm tabular-nums text-gray-600">{l.max} per workspace</span>
                    ) : (
                      <span className={`text-sm tabular-nums ${l.used >= l.max ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                        {/* A count is never shown above its limit (the server clamps too: limitStatus.displayedUsed). */}
                        {Math.min(l.used, l.max)} / {l.max} used
                      </span>
                    )}
                  </div>
                  {l.scope === 'org' && (
                    <p className="text-xs font-medium text-gray-600 mt-1">
                      Workspace limit{l.planName ? ` · ${l.planName} plan` : ''}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">{l.explanation}</p>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
